import React, { useState } from 'react';
import { UndoToast } from './UndoToast';

/**
 * useWriteFeedback — the write-failure surface for detail panels (design 01-architecture §4.3,
 * corpus D-D2/D-K3/D-I5/D-C5-family). The reference loop (LeadsGrid.applyEdit /
 * PipelineGrid.applyStage) already carries its own toast state; this hook gives the PANELS the same
 * two primitives so their verbs stop failing silently:
 *
 *   fail(msg)            — a failed write is VISIBLE. The unchecked-fetch verbs re-rendered the
 *                          record unchanged, which the operator reads as a mis-tap and taps again —
 *                          on invoices, the tap that sends the customer a second invoice.
 *   undoable(msg, undo)  — an applied write offers a working undo (UndoToast's own contract:
 *                          "REQUIRED, not optional" after an optimistic write on a touch surface).
 *
 * Deliberately NOT here: undo toasts for lifecycle toggles whose inverse stays on screen (detail
 * Complete/Resolve/Reopen — the ratified TaskDetailPanel precedent: a persistent toggle needs no
 * ephemeral twin). Those verbs use fail() only. RULED EXCEPTION — deal close (B1): its success
 * toast is primarily the D-D1 pin DISCLOSURE ("revenue pinned to Acme Corp"), which must render
 * regardless; DN-1 (reopen un-pins, so close is cleanly reversible) then puts Undo on it.
 */
export function useWriteFeedback() {
  const [toast, setToast] = useState<{ message: string; variant: 'undo' | 'error'; onUndo?: () => void; actionLabel?: string; durationMs?: number } | null>(null);
  const fail = (message: string) => setToast({ message, variant: 'error' });
  const undoable = (message: string, onUndo: () => void) =>
    setToast({ message, variant: 'undo', onUndo: () => { setToast(null); onUndo(); } });
  /** The THIRD outcome — the write LANDED but the confirming refetch failed. "It did not happen"
   *  and "it happened and we could not confirm it" are different facts and must not share a
   *  representation: rendering this as saveFailed invites a retry of a write that already landed
   *  (on invoice send, a double-send attempt the service gate has to absorb). The action button
   *  RE-CHECKS the durable state — it never re-submits. Longer duration: this one carries the
   *  only affordance that resolves the ambiguity, so it should not vanish mid-read. */
  const unconfirmed = (message: string, actionLabel: string, onRecheck: () => void) =>
    setToast({ message, variant: 'error', actionLabel, durationMs: 10000, onUndo: () => { setToast(null); onRecheck(); } });
  const clear = () => setToast(null);
  const node = toast
    ? <UndoToast message={toast.message} variant={toast.variant} onUndo={toast.onUndo} actionLabel={toast.actionLabel} durationMs={toast.durationMs} onExpire={() => setToast(null)} />
    : null;
  return { fail, undoable, unconfirmed, clear, node };
}
